/**
 * Whether the sign-in screen should lead with "Unlock" instead of the menu (G9).
 *
 * ── The problem ──
 *
 * A session here lasts sixty idle minutes. On the installed app that means
 * the reader is signed OUT almost every time they open it — a push arrives,
 * they tap it, and they are looking at five ways to sign in. For somebody who
 * always uses the same passkey that is a menu nobody needs; they need the one
 * door they always use, under their thumb.
 *
 * A passkey collapses it to one tap and a face. It is not a weaker session —
 * it is the same sign-in, through the credential this app already treats as a
 * full second factor on its own (server/twoFactor.ts `needs2FA`). Nothing is
 * stored about the account; only a flag that THIS DEVICE has signed in with a
 * passkey before.
 *
 * ── All three conditions ──
 *
 * `standalone` — in a browser tab the reader has a password manager and tabs
 *   of context; the menu is the right screen there. The installed app is where
 *   the form's cost is real, and the only surface that receives push at all.
 * `supported` — an Unlock button without WebAuthn is a dead end.
 * `onThisDevice` — asked of the DEVICE, never the account: passkeys sync, and
 *   an account's passkey count says nothing about the phone in somebody's hand.
 *
 * It is an offer, never a wall: the screen always keeps "Use another way", and
 * drops to the full menu by itself the moment the ceremony fails.
 */

import { hasPasskeyOnThisDevice as deviceFlag, markPasskeyOnThisDevice } from "./passkeyDevice";

/**
 * The device flag is lib/passkeyDevice's (P2's enrolment offer reads the same
 * one); this module only adds the UNLOCK question on top of it.
 */
export { markPasskeyOnThisDevice as rememberPasskeyOnThisDevice };

/**
 * Whether this device holds a SaleFish passkey — with UNKNOWN answered "no".
 *
 * lib/passkeyDevice answers "yes" when storage cannot be read, because for an
 * enrolment OFFER a nudge that can never be silenced is the worse failure. For
 * Unlock it is the other way round: a one-button screen whose button cannot
 * work is a dead end, so a device that cannot answer gets the ordinary menu.
 */
export function hasPasskeyOnThisDevice(): boolean {
  try {
    window.localStorage.getItem("salefish:probe");
  } catch {
    return false;
  }
  return deviceFlag();
}

/** Launched from the Home Screen / as an installed app, rather than in a tab. */
export function isStandaloneDisplay(): boolean {
  try {
    if (typeof window === "undefined") return false;
    if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
    return window.matchMedia?.("(display-mode: standalone)").matches ?? false;
  } catch {
    return false;
  }
}

/** The decision, pure and exported for the test. */
export function shouldOfferResume(o: { standalone: boolean; supported: boolean; onThisDevice: boolean }): boolean {
  return o.standalone && o.supported && o.onThisDevice;
}

/**
 * The same question against the live environment. Read once, when the sign-in
 * screen mounts: display mode cannot change without a relaunch, and a screen
 * that rearranged itself mid-tap would be worse than either arrangement.
 */
export function canResumeWithPasskey(supported: boolean): boolean {
  return shouldOfferResume({
    standalone: isStandaloneDisplay(),
    supported,
    onThisDevice: hasPasskeyOnThisDevice(),
  });
}
